/**
 * Utility functions for managing scroll behavior and focus
 */

/**
 * Scrolls to the top of the page with respect to user preferences
 */
export const scrollToTop = (behavior: 'auto' | 'smooth' = 'smooth') => {
  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: prefersReducedMotion ? 'auto' : behavior
  });
};

/**
 * Scrolls to a specific element with optional offset
 */
export const scrollToElement = (
  element: HTMLElement | string,
  offset: number = 0,
  behavior: 'auto' | 'smooth' = 'smooth'
) => {
  const targetElement = typeof element === 'string' 
    ? document.querySelector(element) as HTMLElement
    : element;
    
  if (!targetElement) return;
  
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const elementTop = targetElement.offsetTop - offset;
  
  window.scrollTo({
    top: elementTop,
    left: 0,
    behavior: prefersReducedMotion ? 'auto' : behavior
  });
};

/**
 * Prevents scroll restoration when navigating back/forward
 */
export const disableScrollRestoration = () => {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
};

/**
 * Enables scroll restoration when navigating back/forward
 */
export const enableScrollRestoration = () => {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'auto';
  }
};

/**
 * Manages focus without triggering scroll
 */
export const focusWithoutScroll = (element: HTMLElement | string) => {
  const targetElement = typeof element === 'string' 
    ? document.querySelector(element) as HTMLElement
    : element;
    
  if (!targetElement) return;
  
  // Store current scroll position
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  
  // Focus the element
  targetElement.focus({ preventScroll: true });
  
  // Restore scroll position if focus caused scroll
  if (window.pageYOffset !== scrollTop || window.pageXOffset !== scrollLeft) {
    window.scrollTo(scrollLeft, scrollTop);
  }
};

/**
 * Debounced scroll handler to prevent excessive scroll events
 */
export const createDebouncedScrollHandler = (
  callback: () => void,
  delay: number = 100
) => {
  let timeoutId: NodeJS.Timeout;
  
  return () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
}; 